import type { ReactNode } from "react"
import { Link, useRouter } from "@tanstack/react-router"
import { AlertCircle, CircleHelp, Lock, Search, Workflow } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError } from "@/lib/api"
import { useConnectionState } from "@/lib/connection"

/**
 * Shared exception states (screen 49): every state names a reason and a safe next step, keeps the page
 * structure stable, and never shows unknown data as zero.
 */

export function RoutePending() {
  return (
    <div className="page-wrap" aria-busy="true" aria-label="Loading">
      <Skeleton className="mb-3 h-7 w-56" />
      <Skeleton className="mb-8 h-4 w-80" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <div className="mt-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div className="flex gap-3" key={i}>
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StateCard({ icon, title, description, children }: { icon: ReactNode; title: string; description: string; children: ReactNode }) {
  return (
    <div className="page-wrap">
      <Card className="mx-auto mt-10 max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          <CardDescription className="text-xs">{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">{children}</CardContent>
      </Card>
    </div>
  )
}

/** Route error boundary: permission, missing record, or a failed request with a retry. */
export function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter()
  const workspace = (router.state.matches.find((m) => (m.params as { workspace?: string }).workspace)?.params as { workspace?: string } | undefined)?.workspace
  if (error instanceof ApiError && error.status === 403) {
    return (
      <StateCard icon={<Lock className="size-4" />} title="Permission required" description={error.problem.title || "Your role does not allow this action."}>
        <p className="mb-3 w-full text-xs leading-5 text-muted-foreground">Ask a workspace administrator to review your role if you expected access.</p>
        {workspace && (
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/settings/roles" params={{ workspace }}>
              View permissions
            </Link>
          </Button>
        )}
      </StateCard>
    )
  }
  if (error instanceof ApiError && error.status === 404) {
    return (
      <StateCard icon={<Search className="size-4" />} title="Not found" description="This record is not in your workspace, or it was removed.">
        {workspace && (
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/overview" params={{ workspace }}>
              Back to overview
            </Link>
          </Button>
        )}
      </StateCard>
    )
  }
  return (
    <StateCard icon={<AlertCircle className="size-4" />} title="Something did not load" description={error instanceof ApiError ? error.problem.title : "The request failed. Nothing was changed."}>
      <Button variant="outline" onClick={() => reset()}>
        <Workflow /> Try again
      </Button>
    </StateCard>
  )
}

export function RouteNotFound() {
  return <RouteError error={new ApiError({ type: "about:blank", title: "Not found", status: 404, code: "not_found", requestId: "" })} reset={() => window.location.reload()} />
}

/** Screen 49: connection interrupted. Received messages stay available; periodic catch-up continues. */
export function ConnectionBanner() {
  const state = useConnectionState()
  if (state !== "reconnecting") return null
  return (
    <div role="status" className="flex flex-wrap items-center gap-3 border-b bg-muted/60 px-4 py-2 text-xs">
      <AlertCircle className="size-3.5" />
      <span className="font-medium">Live updates interrupted.</span>
      <span className="text-muted-foreground">Received messages remain available; reconnecting refreshes the full view.</span>
      <Button variant="outline" size="sm" className="ml-auto h-7" onClick={() => window.location.reload()}>
        <Workflow /> Reconnect &amp; refresh
      </Button>
    </div>
  )
}

/** Screen 49: delivery is uncertain. Used wherever an unknown submission is shown. */
export function UncertainDeliveryNote({ children }: { children?: ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
      <CircleHelp className="mt-0.5 size-3.5 shrink-0" />
      <span>{children ?? "Unknown is a state, not a failed send. Wait for reconciliation before retrying; acceptance, delivery and reading are separate signals."}</span>
    </p>
  )
}
